import { music24PlanCapabilities, MUSIC24_TRIAL_LEVEL_TIER } from 'fym-shared/src/music24/music24Plans';

/** One way the trial differs from the plan whose limits it gives (Pro). */
export type TrialDifference =
  /** The trial credits a month and the credits a month of the full plan (null: no limit). */
  | { kind: 'videoCredits'; credits: number; full: number | null }
  | { kind: 'previews' }
  | { kind: 'renderLast' }
  | { kind: 'badge' }
  | { kind: 'noCustomDomain' }
  | { kind: 'noX' }
  | { kind: 'oneArtist' }
  | { kind: 'noAiClips' }
  | { kind: 'pitches'; perDay: number }
  | { kind: 'noAutopilot' }
  | { kind: 'autoPostsPerDay'; perDay: number };

/**
 * The trial differences of Settings → Subscription (QA 2026-10-28, skp-8). They come from the
 * trial row of `music24PlanCapabilities` (the Pro row with `MUSIC24_TRIAL_PRO_EXCEPTIONS`,
 * `MUSIC24_TRIAL_OUTREACH` and the trial lane and preview rules): a line shows only when the trial
 * value is not the Pro value, so a change of the plans changes the list with no copy edit.
 */
export function trialDifferences(): TrialDifference[] {
  const { trial } = music24PlanCapabilities;
  const full = music24PlanCapabilities[MUSIC24_TRIAL_LEVEL_TIER];
  const lines: TrialDifference[] = [];
  if (trial.videoCreditsPerMonth !== null && trial.videoCreditsPerMonth !== full.videoCreditsPerMonth) {
    lines.push({ kind: 'videoCredits', credits: trial.videoCreditsPerMonth, full: full.videoCreditsPerMonth });
  }
  if (trial.previewRenders && !full.previewRenders) {
    lines.push({ kind: 'previews' });
  }
  if (trial.renderLane === 'last' && full.renderLane !== 'last') {
    lines.push({ kind: 'renderLast' });
  }
  if (!trial.canRemoveBranding && full.canRemoveBranding) {
    lines.push({ kind: 'badge' });
  }
  if (!trial.canUseCustomDomain && full.canUseCustomDomain) {
    lines.push({ kind: 'noCustomDomain' });
  }
  if (!trial.canUseX && full.canUseX) {
    lines.push({ kind: 'noX' });
  }
  if (trial.extraArtistLimit === 0 && full.extraArtistLimit !== 0) {
    lines.push({ kind: 'oneArtist' });
  }
  if (!trial.canMakeAiFootage && full.canMakeAiFootage) {
    lines.push({ kind: 'noAiClips' });
  }
  if (trial.outreachPitchesPerDay !== full.outreachPitchesPerDay) {
    // Only the daily rate: `outreachSendsPerMonth` is a technical cap that no screen prints.
    lines.push({ kind: 'pitches', perDay: trial.outreachPitchesPerDay });
  }
  if (!trial.canUseOutreachAutopilot && full.canUseOutreachAutopilot) {
    lines.push({ kind: 'noAutopilot' });
  }
  if (trial.reelsPlanMaxPostsPerDay < full.reelsPlanMaxPostsPerDay) {
    lines.push({ kind: 'autoPostsPerDay', perDay: trial.reelsPlanMaxPostsPerDay });
  }
  return lines;
}
